import { createSlice } from "@reduxjs/toolkit";
import type {
  ControlType,
  FiveLevelControl,
} from "../types/userTypes";

/* ---------------- PRESET TYPES ---------------- */

type ControlValue = "allow" | "deny" | "view" | "own";

interface PresetPermissionMap {
  [action: string]: ControlValue;
}

interface PresetBlueprint {
  [module: string]: PresetPermissionMap;
}

export interface PresetConfig {
  id: string;
  name: string;
  description: string;
  /** Shown under the preset grid when this preset is selected (UI hint). */
  selectionHint?: string;
  /**
   * Extra note for presets where branch assignment matters (e.g. Admin, Manager).
   * Shown together with selectionHint when the user picks this preset.
   */
  branchScopeHint?: string;
  /**
   * Short bullet list in plain language: what people in this preset can typically do.
   * Shown in the role wizard when this preset is selected.
   */
  presetDetails?: string[];
  enableAllStages: boolean;
  enableAllSources: boolean;
  enableAllModules: boolean;
  /** Per-stage modules only (no lead_management — use leadManagementBlueprint). */
  stageBlueprint: PresetBlueprint;
  sourceBlueprint: PresetBlueprint;
  globalOverrides: PresetPermissionMap;
  /** Single global.lead_management per role (assignment is under Assignation). */
  leadManagementBlueprint: PresetPermissionMap;
  /** global.assignation_management — lead/child/branch toggles. */
  assignationManagementBlueprint: PresetPermissionMap;
  /** Default control for every dashboard widget when this preset is applied.
   *  Widget keys are dynamic (industry-dependent), so this seeds all of them. */
  dashboardDefaultControl: ControlValue;
}

const DOCUMENT_FULL: PresetPermissionMap = {
  create: "allow",
  manage: "allow",
  view: "allow",
  edit: "allow",
  update: "allow",
  delete: "allow",
  download: "allow",
};

const DOCUMENT_MANAGER: PresetPermissionMap = {
  create: "allow",
  manage: "allow",
  view: "allow",
  edit: "allow",
  update: "allow",
  delete: "deny",
  download: "allow",
};

const DOCUMENT_EDITOR: PresetPermissionMap = {
  create: "allow",
  manage: "allow",
  view: "allow",
  edit: "allow",
  update: "allow",
  delete: "deny",
  download: "allow",
};

// Counsellor doc access: full edit-tier (create / view / edit / update / download)
// but never manage or delete.
const DOCUMENT_CONTRIBUTOR: PresetPermissionMap = {
  create: "allow",
  manage: "deny",
  view: "allow",
  edit: "allow",
  update: "allow",
  delete: "deny",
  download: "allow",
};

const DOCUMENT_VIEWER: PresetPermissionMap = {
  create: "deny",
  manage: "deny",
  view: "allow",
  edit: "deny",
  update: "deny",
  delete: "deny",
  download: "deny",
};

const DOCUMENT_GUEST: PresetPermissionMap = {
  create: "deny",
  manage: "deny",
  view: "deny",
  edit: "deny",
  update: "deny",
  delete: "deny",
  download: "deny",
};

/* ---------------- PRESET CONFIGS ---------------- */

const ALL_ALLOW_LEAD: PresetPermissionMap = {
  create: "allow",
  view: "allow",
  edit: "allow",
  delete: "allow",
  bulk_edit: "allow",
  view_activity: "allow",
  view_deadline: "allow",
  set_deadline: "allow",
  view_archived_history: "allow",
};

const ALL_ALLOW_ASSIGNATION: PresetPermissionMap = {
  lead_assignation: "allow",
  child_assignation: "allow",
  branch_assignation: "allow",
  inbound_branchless: "allow",
};

const ASSIGNATION_DENY_ALL: PresetPermissionMap = {
  lead_assignation: "deny",
  child_assignation: "deny",
  branch_assignation: "deny",
  inbound_branchless: "deny",
};

/** Counsellor-style: work own leads, limited branch admin.
 *  `inbound_branchless` stays deny — receiving no-branch leads is an
 *  explicit per-role opt-in (flip it on for junior counsellor roles). */
const ASSIGNATION_CONTRIBUTOR: PresetPermissionMap = {
  lead_assignation: "allow",
  child_assignation: "deny",
  branch_assignation: "deny",
  inbound_branchless: "deny",
};

/** Editor: can assign leads, children, and branches */
const ASSIGNATION_EDITOR: PresetPermissionMap = {
  lead_assignation: "allow",
  child_assignation: "allow",
  branch_assignation: "allow",
  inbound_branchless: "deny",
};

const ALL_ALLOW_STAGE: PresetBlueprint = {
  stage_management: {
    create: "allow",
    manage: "allow",
    update: "allow",
    move: "allow",
    rollback: "allow",
    delete: "allow",
    // No preset seeds per-stage "Export leads" (`export`): left absent, every
    // stage inherits Global → Bulk export, so flipping that switch later
    // still works. An explicit value is only ever the admin's own choice.
    filter_view: "allow",
    filter_edit: "allow",
  },
  choice_management: {
    create: "allow",
    edit: "allow",
    view: "allow",
    delete: "allow",
  },
  document_management: DOCUMENT_FULL,
  track_management: {
    create: "allow",
    view: "allow",
    edit: "allow",
    delete: "allow",
  },
};

const ALL_ALLOW_SOURCE: PresetBlueprint = {
  source_access: { view: "allow", create: "allow", assign: "allow" },
};

const CRUD_ALLOW: PresetPermissionMap = {
  create: "allow",
  edit: "allow",
  view: "allow",
  delete: "allow",
};
const CRUD_NO_DELETE: PresetPermissionMap = {
  create: "allow",
  edit: "allow",
  view: "allow",
  delete: "deny",
};
const CRUD_VIEW_EDIT: PresetPermissionMap = {
  create: "deny",
  edit: "allow",
  view: "allow",
  delete: "deny",
};
const CRUD_VIEW_ONLY: PresetPermissionMap = {
  create: "deny",
  edit: "deny",
  view: "allow",
  delete: "deny",
};
const CRUD_DENY: PresetPermissionMap = {
  create: "deny",
  edit: "deny",
  view: "deny",
  delete: "deny",
};

export const PRESET_CONFIGS: PresetConfig[] = [
  {
    id: "admin",
    name: "Admin",
    description: "Full access to all features and settings",
    selectionHint:
      "Best for workspace owners and senior admins. This preset turns on the main CRM, your industry tools, reports, and team settings. You can still tighten anything on the next steps.",
    branchScopeHint:
      "Always assign each user to the right branches (like on the Add user screen). Branches control which leads and offices they actually work with—even admins only see data their branch rules allow.",
    presetDetails: [
      "Leads: create, edit, delete, bulk edit, full activity history.",
      "Pipeline: all stages and sources enabled; full control over moves, documents, tracks, and stage setup.",
      "Team & setup: manage users, roles, branches, and custom field definitions; industry modules (e.g. universities, courses) as your tenant uses them.",
      "Assignation: can assign leads, related records, and branches where your workspace supports it.",
      "After choosing Admin, review Modules and Permission to match your policy—nothing is locked.",
    ],
    enableAllStages: true,
    enableAllSources: true,
    enableAllModules: true,
    stageBlueprint: ALL_ALLOW_STAGE,
    sourceBlueprint: ALL_ALLOW_SOURCE,
    globalOverrides: CRUD_ALLOW,
    leadManagementBlueprint: ALL_ALLOW_LEAD,
    assignationManagementBlueprint: ALL_ALLOW_ASSIGNATION,
    dashboardDefaultControl: "allow",
  },
  {
    id: "manager",
    name: "Manager",
    description: "Manage teams, leads, and most settings",
    selectionHint:
      "For team leads who run day-to-day operations. Similar reach to Admin, but destructive actions (like deleting some pipeline pieces or leads) are limited so mistakes are harder.",
    branchScopeHint:
      "Add them only to branches they manage. They’ll see leads and users tied to those branches—same idea as picking branches when you add a user.",
    presetDetails: [
      "Leads: strong create/edit and assignment; delete on leads is off by default in this preset.",
      "Pipeline: all stages and sources; stage/source deletes are restricted compared to Admin.",
      "Team & setup: can manage users and most settings; industry and sidebar modules stay available.",
      "Assignation: full assign/reassign flows where enabled.",
      "Use Preview at the end to confirm counts before saving.",
    ],
    enableAllStages: true,
    enableAllSources: true,
    enableAllModules: true,
    stageBlueprint: {
      stage_management: {
        create: "allow",
        manage: "allow",
        update: "allow",
        move: "allow",
        rollback: "allow",
        delete: "deny",
        filter_view: "allow",
        filter_edit: "allow",
      },
      choice_management: {
        create: "allow",
        edit: "allow",
        view: "allow",
        delete: "deny",
      },
      document_management: DOCUMENT_MANAGER,
      track_management: {
        create: "allow",
        view: "allow",
        edit: "allow",
        delete: "deny",
      },
    },
    sourceBlueprint: ALL_ALLOW_SOURCE,
    globalOverrides: CRUD_NO_DELETE,
    leadManagementBlueprint: {
      create: "allow",
      view: "allow",
      edit: "allow",
      delete: "deny",
      bulk_edit: "allow",
      view_activity: "allow",
      view_deadline: "allow",
      set_deadline: "allow",
      view_archived_history: "allow",
    },
    assignationManagementBlueprint: ALL_ALLOW_ASSIGNATION,
    dashboardDefaultControl: "allow",
  },
  {
    id: "editor",
    name: "Editor",
    description: "Edit leads and data, limited management access",
    selectionHint:
      "For experienced staff who live in the pipeline: they update leads, documents, and tracks, but shouldn’t restructure the whole system or manage every user.",
    branchScopeHint:
      "Limit their branches so they only edit leads that belong to their team or region.",
    presetDetails: [
      "Leads: create and edit; bulk edit often stays on. Use Global → Assignation for who can assign leads.",
      "Pipeline: can move leads and use tracks/documents; creating or deleting whole stages is off.",
      "Sources: can view and create from sources; assigning “source owner” style actions may be limited.",
      "Sidebar: industry and management modules usually stay on so they can update linked records (courses, services, etc.).",
      "Assignation: often allowed to assign leads, children, or branches—verify under Global → Assignation.",
    ],
    enableAllStages: true,
    enableAllSources: true,
    enableAllModules: true,
    stageBlueprint: {
      stage_management: {
        create: "deny",
        manage: "deny",
        update: "deny",
        move: "allow",
        rollback: "deny",
        delete: "deny",
        filter_view: "allow",
        filter_edit: "allow",
      },
      choice_management: {
        create: "allow",
        edit: "allow",
        view: "allow",
        delete: "deny",
      },
      document_management: DOCUMENT_EDITOR,
      track_management: {
        create: "allow",
        view: "allow",
        edit: "allow",
        delete: "deny",
      },
    },
    sourceBlueprint: {
      source_access: { view: "allow", create: "allow", assign: "deny" },
    },
    globalOverrides: CRUD_VIEW_EDIT,
    leadManagementBlueprint: {
      create: "allow",
      view: "allow",
      edit: "allow",
      delete: "deny",
      bulk_edit: "allow",
      view_activity: "allow",
      view_deadline: "allow",
      set_deadline: "allow",
      view_archived_history: "deny",
    },
    assignationManagementBlueprint: ASSIGNATION_EDITOR,
    dashboardDefaultControl: "allow",
  },
  {
    id: "contributor",
    name: "Counsellor",
    description: "Edit-tier access across leads, stages, tracks, and documents",
    selectionHint:
      "Frontline edit role: counsellors can create, view and edit everything they touch (leads, choices, tracks, documents, custom fields). They never get manage / admin tier and can't delete or run destructive bulk actions.",
    branchScopeHint:
      "They only see leads their branch (and your rules) allow—same as assigning branch or country on a user profile.",
    presetDetails: [
      "Leads: add, view and edit leads they can access. No delete, no bulk edit.",
      "Pipeline: can move leads, view stage filters, and edit documents/tracks/choices. Can't create or delete stages.",
      "Modules: sidebar is core CRM by default — turn extra industry modules on under the Modules step.",
      "Assignation: can assign leads (outbound). Branches and children stay off unless you enable them.",
      "Custom fields: edit-tier on every field by default — tighten sensitive ones individually.",
    ],
    enableAllStages: true,
    enableAllSources: true,
    enableAllModules: false,
    stageBlueprint: {
      stage_management: {
        create: "deny",
        manage: "deny",
        update: "deny",
        move: "allow",
        rollback: "deny",
        delete: "deny",
        filter_view: "allow",
        filter_edit: "allow",
      },
      choice_management: {
        create: "allow",
        edit: "allow",
        view: "allow",
        delete: "deny",
      },
      document_management: DOCUMENT_CONTRIBUTOR,
      track_management: {
        create: "allow",
        view: "allow",
        edit: "allow",
        delete: "deny",
      },
    },
    sourceBlueprint: {
      source_access: { view: "allow", create: "allow", assign: "deny" },
    },
    // Counsellor = edit-tier (create / view / edit allowed, delete denied).
    // Never CRUD_VIEW_ONLY and never CRUD_ALLOW (which would imply manage).
    globalOverrides: CRUD_NO_DELETE,
    leadManagementBlueprint: {
      create: "allow",
      view: "allow",
      edit: "allow",
      delete: "deny",
      bulk_edit: "deny",
      view_activity: "allow",
      view_deadline: "allow",
      set_deadline: "allow",
      view_archived_history: "deny",
    },
    assignationManagementBlueprint: ASSIGNATION_CONTRIBUTOR,
    dashboardDefaultControl: "allow",
  },
  {
    id: "viewer",
    name: "Viewer",
    description: "View-only access across all data",
    selectionHint:
      "Read-only: they can open leads and follow activity but shouldn’t change data. Same broad module idea as Counsellor—core CRM, not full industry tools—unless you expand it on the Modules step.",
    branchScopeHint:
      "They only view leads visible to their branch; they never need edit rights on the user screen beyond their role.",
    presetDetails: [
      "Leads: view and activity only—no create, edit, delete, or assignment.",
      "Pipeline: can see stages and tracks; cannot move leads or upload documents (per preset).",
      "Sources: view only—cannot create leads from a source.",
      "Assignation: all off—they don’t reassign work.",
      "Use for auditors, partners, or managers who only need visibility.",
    ],
    enableAllStages: true,
    enableAllSources: true,
    enableAllModules: false,
    stageBlueprint: {
      stage_management: {
        create: "deny",
        manage: "deny",
        update: "deny",
        move: "deny",
        rollback: "deny",
        delete: "deny",
        filter_view: "deny",
        filter_edit: "deny",
      },
      choice_management: {
        create: "deny",
        edit: "deny",
        view: "allow",
        delete: "deny",
      },
      document_management: DOCUMENT_VIEWER,
      track_management: {
        create: "deny",
        view: "allow",
        edit: "deny",
        delete: "deny",
      },
    },
    sourceBlueprint: {
      source_access: { view: "allow", create: "deny", assign: "deny" },
    },
    globalOverrides: CRUD_VIEW_ONLY,
    leadManagementBlueprint: {
      create: "deny",
      view: "allow",
      edit: "deny",
      delete: "deny",
      bulk_edit: "deny",
      view_activity: "allow",
      view_deadline: "allow",
      set_deadline: "deny",
      view_archived_history: "deny",
    },
    assignationManagementBlueprint: ASSIGNATION_DENY_ALL,
    // EVERY preset seeds every dashboard widget as "allow" (user directive
    // 2026-09-20): a widget shows the viewer's OWN scoped data, so a narrow
    // role reading its own numbers is the point — Viewer/Guest used to start
    // with the whole dashboard denied and looked broken out of the box.
    dashboardDefaultControl: "allow",
  },
  {
    id: "guest",
    name: "Guest",
    description: "Minimal access, view limited data only",
    selectionHint:
      "Starts almost closed: few permissions and modules until you enable them. The permission numbers look small on purpose. Use Guest when someone should barely touch the system, then open only what you need on Modules and Permission.",
    branchScopeHint:
      "Pair with a narrow branch or test account so they see almost nothing until you deliberately expand access.",
    presetDetails: [
      "Leads: mostly view-only with tight limits; creating or editing is generally off.",
      "Pipeline: all stages are listed with deny-heavy permissions until you open specific Allow rows under Permission.",
      "Modules: only a minimal sidebar until you add modules on the Modules step.",
      "Team: no user/role/branch management by default.",
      "Custom fields: review Deny / View only so hidden fields stay hidden.",
    ],
    enableAllStages: true,
    enableAllSources: false,
    enableAllModules: false,
    stageBlueprint: {
      stage_management: {
        create: "deny",
        manage: "deny",
        update: "deny",
        move: "deny",
        rollback: "deny",
        delete: "deny",
        filter_view: "deny",
        filter_edit: "deny",
      },
      choice_management: {
        create: "deny",
        edit: "deny",
        view: "deny",
        delete: "deny",
      },
      document_management: DOCUMENT_GUEST,
      track_management: {
        create: "deny",
        view: "deny",
        edit: "deny",
        delete: "deny",
      },
    },
    sourceBlueprint: {
      source_access: { view: "deny", create: "deny", assign: "deny" },
    },
    globalOverrides: CRUD_DENY,
    leadManagementBlueprint: {
      create: "deny",
      view: "allow",
      edit: "deny",
      delete: "deny",
      bulk_edit: "deny",
      view_activity: "deny",
      view_deadline: "deny",
      set_deadline: "deny",
      view_archived_history: "deny",
    },
    assignationManagementBlueprint: ASSIGNATION_DENY_ALL,
    // EVERY preset seeds every dashboard widget as "allow" (user directive
    // 2026-09-20): a widget shows the viewer's OWN scoped data, so a narrow
    // role reading its own numbers is the point — Viewer/Guest used to start
    // with the whole dashboard denied and looked broken out of the box.
    dashboardDefaultControl: "allow",
  },
];

// `custom` is added programmatically below so it always mirrors Counsellor —
// the user picks Custom on Page 1 to start from the Counsellor seed and then
// hand-tunes everything. Keeping it derived avoids drift if Counsellor changes.
const counsellorPreset = PRESET_CONFIGS.find((p) => p.id === "contributor");
if (counsellorPreset) {
  PRESET_CONFIGS.push({
    ...counsellorPreset,
    id: "custom",
    name: "Custom",
    description: "Start from Counsellor and customise every permission.",
    selectionHint: undefined,
    branchScopeHint: undefined,
    presetDetails: undefined,
    stageBlueprint: structuredClone(counsellorPreset.stageBlueprint),
    sourceBlueprint: structuredClone(counsellorPreset.sourceBlueprint),
    leadManagementBlueprint: structuredClone(
      counsellorPreset.leadManagementBlueprint
    ),
    assignationManagementBlueprint: structuredClone(
      counsellorPreset.assignationManagementBlueprint
    ),
  });
}

export const PRESET_ROLES = PRESET_CONFIGS.map((p) => ({
  id: p.id,
  name: p.name,
}));

export function getPresetConfig(presetId: string): PresetConfig | undefined {
  return PRESET_CONFIGS.find((p) => p.id === presetId);
}

export function countPresetPermissions(
  preset: PresetConfig,
  stageCount: number,
  sourceCount: number,
  globalModuleCount: number
): { allowed: number; total: number } {
  let allowed = 0;
  let total = 0;

  if (preset.enableAllStages && stageCount > 0) {
    Object.values(preset.stageBlueprint).forEach((actions) => {
      Object.values(actions).forEach((v) => {
        total += stageCount;
        if (v !== "deny") allowed += stageCount;
      });
    });
  }

  Object.values(preset.leadManagementBlueprint).forEach((v) => {
    total += 1;
    if (v !== "deny") allowed += 1;
  });

  Object.values(preset.assignationManagementBlueprint).forEach((v) => {
    total += 1;
    if (v !== "deny") allowed += 1;
  });

  if (preset.enableAllSources && sourceCount > 0) {
    Object.values(preset.sourceBlueprint).forEach((actions) => {
      Object.values(actions).forEach((v) => {
        total += sourceCount;
        if (v !== "deny") allowed += sourceCount;
      });
    });
  }

  for (let i = 0; i < globalModuleCount; i++) {
    Object.values(preset.globalOverrides).forEach((v) => {
      total++;
      if (v !== "deny") allowed++;
    });
  }

  return { allowed, total };
}

/**
 * Wizard tabs for the redesigned Add/Edit Role flow.
 * Widgets is no longer a separate step — dashboard widget controls are folded
 * into the Modules step (under the Dashboard module) per the RBAC redesign.
 */
export const popTabRole = [
  { label: "Basic Details", value: "Basic Details", iconCount: 1 },
  { label: "Modules", value: "Modules", iconCount: 2 },
  { label: "Permission", value: "Permission", iconCount: 3 },
  { label: "Preview", value: "Preview", iconCount: 4 },
];

/* ---------------- PRIVILEGE OPTIONS (Page 1 selector) ---------------- */
//
// The Add Role wizard now exposes 4 privileges instead of 6 preset cards.
// Each option maps to one of the existing PRESET_CONFIGS (so the seeding
// logic in FormRoleContext.applyPreset keeps working unchanged) — except
// "custom", which is a brand-new entry that seeds from Counsellor and then
// expects the user to tweak everything by hand.
//
// `presetId` is the value persisted on `RoleFormData.presetRole` and sent
// to the backend (see docs/rbac-redesign-backend-changes.md §1).

export interface PrivilegeOption {
  id: "admin" | "manager" | "counsellor" | "custom";
  label: string;
  /** Short helper line shown under the selector when this option is picked. */
  helper: string;
  /** id matching one of the entries in PRESET_CONFIGS used to seed the form. */
  presetId: string;
}

export const PRIVILEGE_OPTIONS: PrivilegeOption[] = [
  {
    id: "admin",
    label: "Admin",
    helper: "Works on everything across the workspace.",
    presetId: "admin",
  },
  {
    id: "manager",
    label: "Manager",
    helper: "Gets to view, edit and manage stuff across selected branches.",
    presetId: "manager",
  },
  {
    id: "counsellor",
    label: "Counsellor / Sales staff",
    helper: "Gets to work on assigned leads only.",
    // FE preset id is "contributor" today; "counsellor" is the backend canonical.
    // See backend changes doc §1.
    presetId: "contributor",
  },
  {
    id: "custom",
    label: "Custom",
    helper:
      "Seeds from Counsellor — adjust every module, stage, and permission yourself.",
    presetId: "custom",
  },
];

export function getPrivilegeOption(privilegeId?: string | null) {
  if (!privilegeId) return undefined;
  // Accept both the new privilege id and the underlying preset id, so existing
  // roles loaded from the server map to a sensible selector value.
  return (
    PRIVILEGE_OPTIONS.find((p) => p.id === privilegeId) ??
    PRIVILEGE_OPTIONS.find((p) => p.presetId === privilegeId)
  );
}

/* ---------------- LEAD MANAGEMENT FEATURES (Page 4) ---------------- */
//
// The redesigned Permission step replaces the 8 Allow/Deny rows under
// "Lead management" with a single 5-level row PLUS the sub-features below.
// Each entry below is one of those rows; the form stores its value at:
//   globalPermissions[key].level                   // global default
//   stagePermissions[stageIndex].permissions[key].level   // per-stage override
//
// The legacy lead_management.{create,view,edit,delete,…} keys remain in the
// form state for backwards compatibility (see roleAdapter on hydration).

export interface LeadFeatureCustomLevel {
  /** The ControlType written to the form state when this level is picked. */
  value: ControlType;
  /** Button label shown in the UI. */
  label: string;
  /** Hover blurb on the button. */
  tooltip?: string;
}

export interface LeadFeatureMeta {
  /** module key written into globalPermissions / stage permissions */
  key: string;
  label: string;
  description: string;
  /**
   * Subset of the standard 5 levels (admin / manage / edit / view / deny)
   * to render. Defaults to all 5 when omitted. Ignored when `customLevels`
   * is set.
   */
  levels?: FiveLevelControl[];
  /**
   * Fully custom set of level buttons — overrides `levels`. Use for rows
   * where the standard semantics don't fit (e.g. Activity timeline =
   * Full view / Assignation after view / Deny).
   */
  customLevels?: LeadFeatureCustomLevel[];
}

export const LEAD_MGMT_FEATURES: LeadFeatureMeta[] = [
  {
    key: "lead_management",
    label: "Lead management",
    description:
      "Add, edit, view and delete leads. The dial below picks the strongest level for this role; sub-features can be tightened individually.",
  },
  {
    key: "lead_deadline",
    label: "Deadlines",
    description:
      "View and set lead deadlines, mark them complete, and edit dates.",
    // Deadlines are about whether the user can change them or only see them.
    levels: ["edit", "view", "deny"],
  },
  {
    key: "lead_followup",
    label: "Follow-ups",
    description:
      "View, create and reschedule follow-ups on leads and child records.",
    // Edit restored (2026-07-21). It was dropped from the dial on 2026-05-23,
    // which made follow-up editing ungrantable for any role saved after that
    // date — while roles stored BEFORE it kept their `edit` level (the runtime
    // rank ladder never stopped honouring it). Net effect: some tenants could
    // reschedule follow-ups and newer ones couldn't, and re-saving a legacy
    // role through this wizard silently downgraded it. Edit / View / Deny now
    // mirrors Deadlines above.
    levels: ["edit", "view", "deny"],
  },
  {
    key: "lead_activity",
    label: "Activity timeline",
    description: "See activity history on a lead.",
    customLevels: [
      {
        value: "manage",
        label: "Full view",
        tooltip:
          "Full view — see the entire lead activity timeline, including events from before this user was assigned.",
      },
      {
        value: "own",
        label: "Assignation after view",
        tooltip:
          "Assignation after view — the activity timeline AND the call history start from the moment this role was given the lead: when the user was assigned to it, when the lead entered their branch (branch-manage roles), or when the lead got the country they hold (custom-field manage roles). Nothing from before that moment is shown.",
      },
      {
        value: "deny",
        label: "Deny",
        tooltip: "Deny — no access to the activity timeline at all.",
      },
    ],
  },
  {
    key: "lead_bulk_edit",
    label: "Bulk edit",
    description: "Run multi-lead operations on leads in this stage.",
    // Bulk edit is binary by nature — either the role can do it or not.
    customLevels: [
      {
        value: "manage",
        label: "Allow",
        tooltip:
          "Allow — this role can run bulk edits on leads in this stage.",
      },
      {
        value: "deny",
        label: "Deny",
        tooltip: "Deny — bulk edits are unavailable for this stage.",
      },
    ],
  },
  {
    // "Reassign" per stage (owner, 2026-11-04). Stored as
    // `lead_reassign: { level }`; only "deny" limits anything (server
    // stage-reassign.ts), so a role saved before this row existed reads Allow.
    // Presets don't set it.
    key: "lead_reassign",
    label: "Reassign",
    description:
      "Change who holds a lead in this stage: replace the assigned person, unassign, move them between the lead and a deal, or transfer to a peer. Giving an unassigned lead its first person is always allowed. Admins and automations are never limited.",
    customLevels: [
      {
        value: "manage",
        label: "Allow",
        tooltip: "Allow (default) — this role can reassign leads in this stage.",
      },
      {
        value: "deny",
        label: "Block",
        tooltip:
          "Block — this role can't change who holds a lead in this stage. It can still give an unassigned lead its first person.",
      },
    ],
  },
  // NOTE: Lead documents intentionally NOT in this list — documents are
  // configured under the dedicated "Documents" / `document_management`
  // section that lives separately on every stage. We don't expose a second
  // doc dial here.
];

/* ---------------- PER-PRIVILEGE DEFAULTS (used by applyPreset) ---------------- */
//
// Extends the legacy PRESET_CONFIGS with the modules introduced by the
// RBAC redesign: per-stage `lead_*.level`, `bulk_operations`, inbound /
// outbound assignation, per-track `.level` and per-custom-field
// `.level`. Keyed by the FE preset id ("admin" | "manager" |
// "contributor" (= Counsellor) | "custom").
//
// Counsellor / Custom share the same defaults — picking Custom seeds
// the Counsellor profile, which the user then customises.

export interface PrivilegeDefaults {
  /** Per-stage Lead management feature levels, keyed by feature key. */
  leadFeatures: Record<string, ControlType>;
  /** Master level applied to every track in every stage. */
  trackLevel: ControlType;
  /** Yes/no per bulk_operations key. */
  bulkOperations: Record<string, ControlType>;
  /** Yes/no per assignation_management key (inbound + outbound). */
  assignation: Record<string, ControlType>;
  /** Yes/no per call_management key. */
  callOperations: Record<string, ControlType>;
  /** 4-level applied to every custom field's value access. */
  customFieldLevel: ControlType;
}

const ADMIN_DEFAULTS: PrivilegeDefaults = {
  leadFeatures: {
    lead_management: "admin",
    lead_deadline: "edit",
    lead_followup: "view",
    lead_activity: "manage",
    lead_bulk_edit: "manage",
  },
  trackLevel: "manage",
  bulkOperations: {
    lead_upload: "allow",
    lead_assignation: "allow",
    lead_branch_transfer: "allow",
    lead_edit: "allow",
    lead_delete: "allow",
    lead_export: "allow",
    lead_followup: "allow",
    lead_deadline: "allow",
  },
  assignation: {
    outbound_lead: "allow",
    outbound_child: "allow",
    outbound_branch: "allow",
    outbound_peer: "deny", // new opt-in capability — admin enables per role
    inbound_lead: "allow",
    inbound_child: "allow",
    inbound_branch: "allow",
  },
  callOperations: {
    // DEFAULT-DENY: call recording, call-log saving, and save-as-lead are OFF for
    // every preset. They must be turned ON explicitly per role — no role (not even
    // admin/manager) records, logs, or auto-creates leads by default.
    call_recording: "deny",
    call_log_tap: "deny",
    call_save_as_lead: "deny",
  },
  customFieldLevel: "manage",
};

const MANAGER_DEFAULTS: PrivilegeDefaults = {
  leadFeatures: {
    lead_management: "manage",
    lead_deadline: "edit",
    lead_followup: "view",
    lead_activity: "manage",
    lead_bulk_edit: "manage",
  },
  trackLevel: "manage",
  bulkOperations: {
    lead_upload: "allow",
    lead_assignation: "allow",
    lead_branch_transfer: "allow",
    lead_edit: "allow",
    lead_delete: "deny", // destructive — opt-in per role
    lead_export: "allow",
    lead_followup: "allow",
    lead_deadline: "allow",
  },
  assignation: {
    outbound_lead: "allow",
    outbound_child: "allow",
    outbound_branch: "allow",
    outbound_peer: "deny", // new opt-in capability — admin enables per role
    inbound_lead: "allow",
    inbound_child: "allow",
    inbound_branch: "allow",
  },
  callOperations: {
    // DEFAULT-DENY: call recording, call-log saving, and save-as-lead are OFF for
    // every preset. They must be turned ON explicitly per role — no role (not even
    // admin/manager) records, logs, or auto-creates leads by default.
    call_recording: "deny",
    call_log_tap: "deny",
    call_save_as_lead: "deny",
  },
  customFieldLevel: "edit",
};

// Counsellor = edit-tier everywhere. No "manage" or "admin" in any 5-level dial.
// For 3-level dials with no "edit" option (lead_activity: manage/own/deny),
// we fall back to the closest non-elevated option ("own"). For binary
// dials (lead_bulk_edit: manage/deny), the only non-manage choice is "deny".
const COUNSELLOR_DEFAULTS: PrivilegeDefaults = {
  leadFeatures: {
    lead_management: "edit",
    lead_deadline: "edit",
    lead_followup: "view",
    // Activity dial only offers manage / own / deny — "own" is the
    // edit-tier-equivalent (see activity timeline only after assignment).
    lead_activity: "own",
    // Bulk edit dial only offers manage / deny. Counsellor must NOT get
    // manage, so this stays "deny" — bulk edit is opt-in per role.
    lead_bulk_edit: "deny",
  },
  trackLevel: "edit",
  bulkOperations: {
    lead_upload: "deny",
    lead_assignation: "deny",
    lead_branch_transfer: "deny",
    lead_edit: "deny",
    lead_delete: "deny",
    lead_export: "deny",
    lead_followup: "allow",
    lead_deadline: "allow",
  },
  assignation: {
    outbound_lead: "allow",
    outbound_child: "deny",
    outbound_branch: "deny",
    outbound_peer: "deny", // opt-in — admin enables for roles that hand off to peers
    inbound_lead: "allow",
    inbound_child: "allow",
    inbound_branch: "allow",
  },
  callOperations: {
    // DEFAULT-DENY (see other presets): all call-management capabilities OFF unless
    // explicitly enabled per role.
    call_recording: "deny",
    call_log_tap: "deny",
    call_save_as_lead: "deny",
  },
  customFieldLevel: "edit",
};

export const PRIVILEGE_DEFAULTS: Record<string, PrivilegeDefaults> = {
  admin: ADMIN_DEFAULTS,
  manager: MANAGER_DEFAULTS,
  contributor: COUNSELLOR_DEFAULTS, // FE id for Counsellor preset
  counsellor: COUNSELLOR_DEFAULTS, // alias for backend canonical id
  custom: COUNSELLOR_DEFAULTS, // Custom seeds from Counsellor
};

export function getPrivilegeDefaults(
  presetId: string | null | undefined
): PrivilegeDefaults | undefined {
  if (!presetId) return undefined;
  return PRIVILEGE_DEFAULTS[presetId];
}

/* ---------------- RAZORPAY (Global tab) ---------------- */
//
// Storage shape `globalPermissions.razorpay.<key>`, yes/no each. Mirrors the
// server blueprint in src/rbac/permissions/razorpay.permissions.ts — keep the
// two in step. The Razorpay TAB itself is a sidebar module (route key
// "razorpay", shown only while Razorpay is connected).

export interface RazorpayOperationMeta {
  key: string;
  label: string;
  description: string;
}

export const RAZORPAY_OPERATIONS: RazorpayOperationMeta[] = [
  {
    key: "view_console",
    label: "View Razorpay data",
    description:
      "Open the Razorpay page and read payments, refunds, settlements, links, invoices, customers, subscriptions and the rest. Nothing can be changed with this alone.",
  },
  {
    key: "manage_payments",
    label: "Move money",
    description:
      "Capture or refund a payment, settle on demand, create or reverse a Route transfer, accept or contest a dispute and upload dispute evidence.",
  },
  {
    key: "manage_billing",
    label: "Manage billing records",
    description:
      "Create, change, cancel or close orders, payment links, invoices, items, plans, subscriptions, add-ons, customers, saved payment methods, QR codes and virtual accounts.",
  },
  {
    key: "create_payment_link",
    label: "Send payment links from a deal",
    description:
      "The Send (Razorpay) button on a deal card: create a link and send it on WhatsApp or email.",
  },
  {
    key: "view_payment_links",
    label: "See a deal's payment links",
    description: "The payment links list on a deal card.",
  },
  {
    key: "cancel_payment_link",
    label: "Cancel a deal's payment link",
    description: "Cancel an unpaid link from the deal card.",
  },
  {
    key: "manage_integration",
    label: "Connect / disconnect Razorpay",
    description:
      "Settings → Apps: connect with API keys, disconnect, and see the webhook secret.",
  },
];

/** Per preset — the server's RAZORPAY_PRESET_BLUEPRINT, key for key. */
export const RAZORPAY_PRESET_DEFAULTS: Record<
  string,
  Record<string, ControlType>
> = {
  admin: {
    manage_integration: "allow",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "allow",
    view_console: "allow",
    manage_payments: "allow",
    manage_billing: "allow",
  },
  manager: {
    manage_integration: "deny",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "allow",
    view_console: "allow",
    manage_payments: "allow",
    manage_billing: "allow",
  },
  editor: {
    manage_integration: "deny",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "allow",
    view_console: "allow",
    manage_payments: "deny",
    manage_billing: "allow",
  },
  contributor: {
    manage_integration: "deny",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "deny",
    view_console: "allow",
    manage_payments: "deny",
    manage_billing: "deny",
  },
  // Counsellor / Custom seed the Counsellor profile (see PRIVILEGE_DEFAULTS).
  counsellor: {
    manage_integration: "deny",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "deny",
    view_console: "allow",
    manage_payments: "deny",
    manage_billing: "deny",
  },
  custom: {
    manage_integration: "deny",
    create_payment_link: "allow",
    view_payment_links: "allow",
    cancel_payment_link: "deny",
    view_console: "allow",
    manage_payments: "deny",
    manage_billing: "deny",
  },
  viewer: {
    manage_integration: "deny",
    create_payment_link: "deny",
    view_payment_links: "allow",
    cancel_payment_link: "deny",
    view_console: "allow",
    manage_payments: "deny",
    manage_billing: "deny",
  },
  guest: {
    manage_integration: "deny",
    create_payment_link: "deny",
    view_payment_links: "deny",
    cancel_payment_link: "deny",
    view_console: "deny",
    manage_payments: "deny",
    manage_billing: "deny",
  },
};

export function getRazorpayPresetDefaults(
  presetId: string | null | undefined
): Record<string, ControlType> | undefined {
  if (!presetId) return undefined;
  return RAZORPAY_PRESET_DEFAULTS[presetId];
}

/* ---------------- ASSIGNATION FEATURES (Page 4) ---------------- */
//
// Inbound  → can OTHERS assign this kind of work to users in this role.
// Outbound → can users in this role assign this kind of work to others.

export interface AssignationFeatureMeta {
  key: string;
  label: string;
  description: string;
  direction: "inbound" | "outbound";
}

/* ---------------- BULK OPERATIONS (Page 4 — Global) ---------------- */
//
// Workspace-wide "do many at once" actions. Each is a yes/no permission;
// stored at globalPermissions.bulk_operations.<key> = "allow" | "deny".

export interface BulkOperationMeta {
  key: string;
  label: string;
  description: string;
}

export const BULK_OPERATIONS: BulkOperationMeta[] = [
  {
    key: "lead_upload",
    label: "Bulk lead upload",
    description:
      "Import leads from CSV or other sources in batches (the Bulk Import flow).",
  },
  {
    key: "lead_assignation",
    label: "Bulk lead assignation",
    description:
      "Select many leads at once and assign or reassign them to a user.",
  },
  {
    key: "lead_branch_transfer",
    label: "Bulk branch transfer",
    description: "Move many leads between branches in a single operation.",
  },
  {
    key: "lead_edit",
    label: "Bulk edit fields",
    description:
      "Mass-update lead fields (priority, source, custom fields) on selected leads.",
  },
  {
    key: "lead_delete",
    label: "Bulk delete",
    description:
      "Destructive — permanently remove many leads in a single operation.",
  },
  {
    key: "lead_export",
    label: "Bulk export",
    description: "Download many leads at once as CSV or via the data export flow.",
  },
  {
    key: "lead_followup",
    label: "Bulk follow-up scheduling",
    description: "Set the same follow-up on many leads at once.",
  },
  {
    key: "lead_deadline",
    label: "Bulk deadline",
    description: "Set or clear deadlines on many leads at once.",
  },
];

/* ---------------- CALL MANAGEMENT (Page 4 — Global) ---------------- */
//
// Workspace-wide call-log capabilities. Stored at
// globalPermissions.call_management.<key> = "allow" | "deny".

export interface CallOperationMeta {
  key: string;
  label: string;
  description: string;
}

export const CALL_OPERATIONS: CallOperationMeta[] = [
  {
    key: "call_recording",
    label: "Call recording access",
    description:
      "Listen to or download recordings attached to call logs (TeleCMI / phone integrations).",
  },
  {
    key: "call_log_tap",
    label: "Call log tap (open detail)",
    description:
      "Open a call log row to see notes, recording, and history. Without this, the call list is read-only summary.",
  },
  {
    key: "call_save_as_lead",
    label: "Save call as lead",
    description:
      "Convert an unmatched incoming call into a new lead from the call log.",
  },
];

/**
 * "Assigned as" — the LEVEL this role's users hold an assignment at. Both on
 * by default (a missing key reads as on). Deal user only → every assignment
 * to this role (drawer, bulk assign, bulk update, automations) lands on the
 * lead's deals and nobody is asked "lead or deal?"; Lead user only → always
 * the lead. Stored beside the inbound/outbound keys as
 * `assignation_management.lead_user` / `.deal_user`, enforced server-side
 * (core/assignment/holder-level.ts).
 */
export const HOLDER_LEVEL_FEATURES: { key: "lead_user" | "deal_user"; label: string; description: string }[] = [
  {
    key: "lead_user",
    label: "Lead user",
    description:
      "Users in this role can be assigned at LEAD level. Turn off (keeping Deal user on) to make this a deal-only role: assigning one of its users to a lead puts them on that lead's deals instead.",
  },
  {
    key: "deal_user",
    label: "Deal user",
    description:
      "Users in this role can be assigned on DEALS (applications). Turn off (keeping Lead user on) to make this a lead-only role: assigning one of its users to a deal puts them on the deal's lead instead.",
  },
];

/**
 * "One person on the lead and all its deals" (owner, 2026-10-25, Adi: "some
 * leads getting two counsellor... it is really illegal"). Off by default (a
 * missing key reads as off). On → whoever of this role is assigned on a lead
 * or one of its deals — by hand, bulk, an automation or a call — also takes
 * this role's other slots on that lead, so it never shows two people of the
 * role. Stored as `assignation_management.single_holder`, enforced server-side
 * (core/assignment/assignment.service.ts).
 */
export const SINGLE_HOLDER_FEATURE = {
  key: "single_holder",
  label: "One person on the lead and all its deals",
  description:
    "When someone in this role is assigned to a lead or to any of its deals (by hand, bulk, an automation or a call), they also take this role's place on the lead's other deals, and anyone else in the role on that lead is removed. A lead never shows two people of this role. Off: each deal can have its own person.",
} as const;

export const ASSIGNATION_FEATURES: AssignationFeatureMeta[] = [
  {
    key: "outbound_lead",
    label: "Outbound — assign leads",
    description: "This role can assign or reassign leads to other users.",
    direction: "outbound",
  },
  {
    key: "outbound_child",
    label: "Outbound — assign child records",
    description:
      "This role can assign child / choice records (linked to a lead) to others.",
    direction: "outbound",
  },
  {
    key: "outbound_branch",
    label: "Outbound — change branch",
    description: "This role can move leads or users between branches.",
    direction: "outbound",
  },
  {
    key: "outbound_peer",
    label: "Transfer to peers (same role)",
    description:
      "Hand off a lead or deal you're assigned to, to another user OF THE SAME ROLE. No role picking — only same-role peers are shown. The recipient must still pass branch + custom-field rules; you lose access once transferred (handover).",
    direction: "outbound",
  },
  {
    key: "inbound_lead",
    label: "Inbound — receive leads",
    description: "Other users may assign leads to users in this role.",
    direction: "inbound",
  },
  {
    key: "inbound_child",
    label: "Inbound — receive child records",
    description: "Other users may assign child records to users in this role.",
    direction: "inbound",
  },
  {
    key: "inbound_branch",
    label: "Inbound — branch transfers",
    description: "Other users may move leads or users into this role's branch.",
    direction: "inbound",
  },
  {
    key: "inbound_branchless",
    label: "Inbound — receive branchless leads",
    description:
      "Users in this role can be assigned leads that have NO branch yet, and will see those assigned leads. Off (default) = branchless leads must get a branch before they can be assigned to this role.",
    direction: "inbound",
  },
];

/* ---------------- STATE ---------------- */

export interface RoleState {
  isAddRole: boolean;

  // Create flow
  currentRoleTab: string;
  isLastTab: boolean;

  // Edit flow
  currentEditRoleTab: string;
  isLastEditTab: boolean;
}

const initialState: RoleState = {
  isAddRole: false,

  currentRoleTab: popTabRole[0].value,
  isLastTab: false,

  currentEditRoleTab: popTabRole[0].value,
  isLastEditTab: false,
};

/* ---------------- SLICE ---------------- */

const roleSlice = createSlice({
  name: "role-functional",
  initialState,
  reducers: {
    /* ---------- COMMON ---------- */

    setIsAddRole(state, action) {
      state.isAddRole = action.payload;
    },

    /* ---------- CREATE ROLE ---------- */

    setCurrentRoleTab(state, action) {
      state.currentRoleTab = action.payload;
      state.isLastTab =
        action.payload === popTabRole[popTabRole.length - 1].value;
    },

    setCurrentRoleTabHandleNext(state) {
      const currentIndex = popTabRole.findIndex(
        (tab) => tab.value === state.currentRoleTab
      );

      const nextIndex = currentIndex + 1;

      if (nextIndex < popTabRole.length) {
        state.currentRoleTab = popTabRole[nextIndex].value;
        state.isLastTab = nextIndex === popTabRole.length - 1;
      }
    },

    setCurrentRoleTabHandleBack(state) {
      const currentIndex = popTabRole.findIndex(
        (tab) => tab.value === state.currentRoleTab
      );

      const prevIndex = currentIndex - 1;

      if (prevIndex >= 0) {
        state.currentRoleTab = popTabRole[prevIndex].value;
        state.isLastTab = false;
      }
    },

    /* ---------- EDIT ROLE ---------- */

    setCurrentEditRoleTab(state, action) {
      state.currentEditRoleTab = action.payload;
      state.isLastEditTab =
        action.payload === popTabRole[popTabRole.length - 1].value;
    },

    setCurrentEditRoleTabHandleNext(state) {
      const currentIndex = popTabRole.findIndex(
        (tab) => tab.value === state.currentEditRoleTab
      );

      const nextIndex = currentIndex + 1;

      if (nextIndex < popTabRole.length) {
        state.currentEditRoleTab = popTabRole[nextIndex].value;
        state.isLastEditTab = nextIndex === popTabRole.length - 1;
      }
    },

    setCurrentEditRoleTabHandleBack(state) {
      const currentIndex = popTabRole.findIndex(
        (tab) => tab.value === state.currentEditRoleTab
      );

      const prevIndex = currentIndex - 1;

      if (prevIndex >= 0) {
        state.currentEditRoleTab = popTabRole[prevIndex].value;
        state.isLastEditTab = false;
      }
    },
  },
});

/* ---------------- EXPORTS ---------------- */

export const {
  setIsAddRole,

  // create
  setCurrentRoleTab,
  setCurrentRoleTabHandleNext,
  setCurrentRoleTabHandleBack,

  // edit
  setCurrentEditRoleTab,
  setCurrentEditRoleTabHandleNext,
  setCurrentEditRoleTabHandleBack,
} = roleSlice.actions;

export default roleSlice.reducer;
